import {
  Button,
  ButtonGroup,
  Divider,
  List,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { faHouseChimney, faVideo } from '@fortawesome/free-solid-svg-icons';
import { isNil } from 'lodash';
import { RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SavedView } from '../../../../gql/graphql';
import PlaceholderText from '../../../text/PlaceholderText';
import RenameDeleteListItem, { Item } from '../../RenameDeleteListItem';
import SparkelIcon from '../../icon/SparkelIcon';

type RenameDeleteViewsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  views: SavedView[];
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetAsHomeView: (id: string) => Promise<void>;
  nameRegisterOptions?: RegisterOptions;
};

export default function EditSavedViewsModal({
  isOpen,
  onClose,
  views,
  onRename,
  onDelete,
  onSetAsHomeView,
  nameRegisterOptions,
}: RenameDeleteViewsModalProps): React.ReactElement {
  const { t } = useTranslation('project');
  const items = views.map((view) => {
    const item: Item = { id: view.id, name: view.name };
    if (view.isDefault) {
      item.icon = (
        <SparkelIcon color={['gray.300', 'gray.500']} icon={faHouseChimney} />
      );
    } else if (view.cameraPosition) {
      item.icon = (
        <SparkelIcon color={['gray.300', 'gray.500']} icon={faVideo} />
      );
    }
    return item;
  });

  const validateUniqueName = (id: string, value: string) => {
    if (
      items.some(
        (item) =>
          item.name.toLowerCase() === value.toLowerCase() && item.id !== id
      )
    ) {
      return t('saved-views.name-already-exists-message');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('saved-views.edit-views')}</ModalHeader>
        <ModalCloseButton />
        <Divider orientation="horizontal" />
        <ModalBody>
          {items.length > 0 ? (
            <List>
              {items.map((item) => (
                <RenameDeleteListItem
                  key={item.id}
                  item={item}
                  validateUniqueName={(value) =>
                    validateUniqueName(item.id, value)
                  }
                  onRename={(value) => onRename(item.id, value)}
                  onDelete={onDelete}
                  nameRegisterOptions={nameRegisterOptions}
                >
                  <MenuItem
                    aria-label="Set as home view"
                    icon={<SparkelIcon icon={faHouseChimney} fixedWidth />}
                    onClick={() => onSetAsHomeView(item.id)}
                    isDisabled={disableSetAsHomeView(item.id, views)}
                  >
                    {t('saved-views.set-as-home')}
                  </MenuItem>
                </RenameDeleteListItem>
              ))}
            </List>
          ) : (
            <PlaceholderText>
              {t('saved-views.no-views-placeholder')}
            </PlaceholderText>
          )}
        </ModalBody>
        <Divider orientation="horizontal" />
        <ModalFooter gap={2} justifyContent="flex-start">
          <ButtonGroup ml="auto">
            <Button colorScheme={'gray'} onClick={onClose}>
              {t('saved-views.cancel')}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function disableSetAsHomeView(id: string, views: SavedView[]) {
  const view = views.find((view) => view.id === id);
  return isNil(view) || isNil(view.cameraPosition) || view.isDefault;
}
